import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { handleError, isAccountant, isManagerOver } from "src/Common/function";
import BranchBar from "src/Components/branchbar/BranchBar";
import UserService from "src/Services/API/user.service";
import AdHoc from "./Ad-Hoc/AdHoc";
import AutoReport from "./Auto/AutoReport";

// Each tab has its own route: /report/ad-hoc and /report/auto.
const TABS = [
  { key: "adhoc", slug: "ad-hoc", label: "Ad-Hoc Reports" },
  { key: "scheduled", slug: "auto", label: "Scheduled Reports" },
];

const ReportCenter = () => {
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));
  const navigate = useNavigate();
  const { tab: tabSlug } = useParams();

  const canViewScheduled = isManagerOver() || isAccountant();

  // Derive the active tab from the URL path segment so each tab is its own route.
  const matchedTab = TABS.find((t) => t.slug === tabSlug);
  const activeTab = matchedTab ? matchedTab.key : "adhoc";

  const setActiveTab = (key) => {
    const tab = TABS.find((t) => t.key === key);
    if (!tab) return;
    // Preserve query params (e.g. ?branch=...) when switching tabs.
    navigate({ pathname: `/report/${tab.slug}`, search: searchParams.toString() }, { replace: true });
  };

  // Sliding underline: measure the active tab's offset/width so the bar animates between tabs.
  const tabRefs = useRef({});
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const node = tabRefs.current[activeTab];
      if (node) setUnderline({ left: node.offsetLeft, width: node.offsetWidth });
    };
    measure();

    // On reload the underline can settle before the custom font finishes loading
    // (semibold tabs are wider than the fallback), leaving it mispositioned.
    // Re-measure once fonts are ready and whenever the window resizes.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [activeTab, canViewScheduled]);

  const [userArray, setUserArray] = useState([]);
  // Toggled by the header button to ask AutoReport to open its create modal.
  // Reset to 0 once consumed so re-mounting AutoReport (e.g. after switching
  // tabs) doesn't re-trigger the modal off a stale non-zero signal.
  const [createScheduledSignal, setCreateScheduledSignal] = useState(0);

  // Normalize the URL: unknown slug, or the scheduled tab without permission,
  // falls back to the ad-hoc route.
  useEffect(() => {
    const invalidSlug = !matchedTab;
    const forbidden = activeTab === "scheduled" && !canViewScheduled;
    if (invalidSlug || forbidden) {
      navigate({ pathname: "/report/ad-hoc", search: searchParams.toString() }, { replace: true });
    }
  }, [matchedTab, activeTab, canViewScheduled, navigate, searchParams]);

  useEffect(() => {
    UserService.getAll(branch)
      .then((response) => {
        if (response.status === 200) {
          setUserArray(response.data.filter((user) => user.isEnabled === true));
        }
      })
      .catch((error) => handleError(error));
  }, [branch]);

  // Ad-hoc reports are generated from the per-card buttons in <AdHoc />.
  // The header action only applies to the Scheduled tab.
  const showHeaderAction = activeTab === "scheduled" && canViewScheduled;

  return (
    <div>
      {/* Tab bar + primary action */}
      <div className="flex items-end justify-between border-b-[1px] border-[#E5E8F3] px-7">
        <div className="relative flex items-center gap-6">
          {TABS.map((tab) => {
            if (tab.key === "scheduled" && !canViewScheduled) return null;
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                ref={(node) => (tabRefs.current[tab.key] = node)}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative py-4 font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] transition-colors ${
                  isActive ? "text-[#0058E5]" : "text-[#7C829A] hover:text-[#0058E5]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
          <div
            className="absolute -bottom-[1px] h-[2px] bg-[#0058E5] rounded-full transition-all duration-300 ease-out"
            style={{ left: underline.left, width: underline.width }}
          />
        </div>

        {showHeaderAction && (
          <button
            type="button"
            onClick={() => setCreateScheduledSignal((value) => value + 1)}
            className="mb-3 h-[32px] px-3 rounded-[8px] bg-blueColor flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] capitalize leading-5 tracking-[0.14px] text-white cursor-pointer hover:opacity-95"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 3.33334V12.6667M3.33334 8H12.6667"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            New Scheduled Report
          </button>
        )}
      </div>

      <div className="px-7 py-2 pb-3 border-b-[1px]">{canViewScheduled && <BranchBar allEnabled={false} />}</div>

      <div className="px-7 py-6">
        {activeTab === "adhoc" || !canViewScheduled ? (
          <AdHoc userArray={userArray} branch={branch} />
        ) : (
          <AutoReport
            createSignal={createScheduledSignal}
            onCreateSignalConsumed={() => setCreateScheduledSignal(0)}
          />
        )}
      </div>
    </div>
  );
};

export default ReportCenter;
